import React, { Component } from 'react'
import { gql } from '@apollo/client'
import classNames from 'classnames'
import { observer, inject } from 'mobx-react'
import { bool, number, oneOfType, string } from 'prop-types'
import { Link } from 'react-router-dom'
import { Checkbox } from '@winnin/winnin_ui.components.checkbox'
import { Title } from '@winnin/winnin_ui.components.title'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import ListMetricVariation from 'components/ListMetricVariation'
import Thumbnail from 'components/Thumbnail'
import TopicListSmall from 'components/TopicListSmall'
import { PROVIDERS, ROLES } from 'consts'
import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider'
import { client } from 'lib'
import { T, useTranslate } from 'lib/tolgee'
import { UserStore } from 'stores'
import { humanizeLargeNumber, stringUtils, countries, makeUrlTo } from 'utils'

import './index.scss'

const SETBRAND_MUTATION = gql`
  mutation setBrand($creatorId: String!, $provider: String!, $isBrand: Boolean!) {
    flagBrand(creatorId: $creatorId, provider: $provider, isBrand: $isBrand)
  }
`

function CreatorCheckbox({ id, provider, title, thumbnail, groupId }) {
  const { selectedItemsBar, addItem, removeItem } = useSelectedItemsList()
  const checkIsActive = selectedItemsBar?.some(check => check.id === id)
  const isViewerUser = UserStore.getRole === 'viewer'
  const t = useTranslate()

  function handleSelectionDefaultPrevented(e) {
    e.preventDefault()
    e.stopPropagation()

    const isItemSelected = selectedItemsBar.find(item => item.id === id)
    if (isItemSelected) {
      removeItem(isItemSelected)
      return
    }
    addItem({ id: id, name: title, type: 'creators', provider: provider, thumbnail: thumbnail, groupId })
  }

  if (isViewerUser) return <div style={{ width: '24px' }} />

  return (
    <TooltipWrapper
      tooltipMessage={t({ key: 'CreatorCheckbox-tooltip', defaultValue: 'Select this creator' })}
      tooltipStyle="tooltippedS"
    >
      <Checkbox
        handleClick={handleSelectionDefaultPrevented}
        isActive={checkIsActive}
        className="creator-list-item-checkbox"
      />
    </TooltipWrapper>
  )
}

const TITLE_LENGTH_LIMIT = 48
@inject('UserStore', 'routingStore', 'FeatureFlagsStore')
@observer
class CreatorListItem extends Component {
  state = {
    isBrand: null,
  }

  isAdminUser = this.props.UserStore.get('role') >= ROLES.ADMIN

  onFlagBrandCall = variables => {
    const { isBrand } = variables

    client.mutate({
      mutation: SETBRAND_MUTATION,
      variables,
    })
    this.setState({ isBrand })
  }

  flagBrand = e => {
    e.stopPropagation()
    e.preventDefault()

    const { id, provider, onFlagBrand } = this.props
    const params = { creatorId: id, provider, isBrand: !this.isBrand() }

    this.onFlagBrandCall(params)
    onFlagBrand?.(params)
  }

  isBrand() {
    const { state, props } = this
    return state.isBrand !== null ? state.isBrand : props.isBrand
  }

  handleSelection(e) {
    e.stopPropagation()
    e.preventDefault()
    const { onSelect } = this.props
    const { id, title, provider, thumbnail, groupId } = this.props

    const creator = {
      title,
      id,
      provider,
      thumbnail,
      groupId,
      creatorId: id,
    }
    onSelect({ creator })
  }

  renderCard() {
    const {
      country,
      color,
      index,
      provider,
      subscribers,
      subscribersVariation,
      thumbnail,
      title,
      likes,
      likesVariation,
      videos,
      videosVariation,
      views,
      viewsVariation,
      isTrending,
      isBrandGroup,
      customListItem = false,
      id,
      topics,
      UserStore,
      FeatureFlagsStore,
      groupId,
    } = this.props
    const providerName = PROVIDERS[provider]?.NAME?.toLowerCase()
    const _isBrand = this.isBrand()
    const isViewerUser = UserStore.getRole === 'viewer'
    const withoutData = '--'

    const creatorListItemContentClass = classNames('CreatorListItemContent', {
      'with-twitch': !FeatureFlagsStore.featureFlagsValues['istiktokenabled'],
      'with-twitch-tiktok': FeatureFlagsStore.featureFlagsValues['istiktokenabled'],
      'custom-list-item': customListItem,
    })

    return (
      <div className={creatorListItemContentClass}>
        {color && <div className="itemColorCreatorList" style={{ backgroundColor: color }}></div>}
        <CreatorCheckbox id={id} provider={provider} title={title} thumbnail={thumbnail} groupId={groupId} />
        <Thumbnail
          alt={title}
          src={thumbnail}
          provider={providerName}
          trending={isTrending}
          className="thumbnail"
          width="66"
          height="66"
        />

        <div className="title-container">
          <Title tag="h5" className="title">
            {stringUtils.truncate(title, TITLE_LENGTH_LIMIT)}
          </Title>
          <div>
            {_isBrand && !isBrandGroup && (
              <span className="brand">
                <T keyName="CreatorListItem-titleContainerBrand">Brand</T>
              </span>
            )}
            {isBrandGroup && (
              <div
                aria-label="This Brand is a Facebook Page grouping of differents countries, which share the same metrics."
                className="tooltipped tooltipped-n tooltipped-multiline CreatorListItem-brand-group"
              >
                <span className="brand">
                  <T keyName="CreatorListItem-titleContainerBrandGroup">Brand Group</T>
                </span>
              </div>
            )}
            {_isBrand && country && ' - '}
            {country && <span className="country">{countries.byValue(country).NAME}</span>}
          </div>
          <TopicListSmall tooltippedText="Creator Topic" topics={topics} />
        </div>

        <div className="CreatorListItem-subscribers">
          <span>
            {providerName === 'youtube' ? (
              <>
                <span data-value={subscribers}>{subscribers !== null ? humanizeLargeNumber(subscribers, 1) : '-'}</span>
                {subscribers ? <ListMetricVariation metricVariation={subscribersVariation} id={index} tooltip /> : null}
              </>
            ) : (
              withoutData
            )}
          </span>
          {providerName === 'youtube' && (
            <span className="CreatorListItem-text">
              <T keyName="CreatorListItem-metricSubscribers">Subscribers</T>
            </span>
          )}
        </div>

        {!customListItem && (
          <>
            <div className="CreatorListItem-subscribers">
              <span>
                {providerName === 'instagram' ? (
                  <>
                    <span data-value={subscribers}>
                      {subscribers !== null ? humanizeLargeNumber(subscribers, 1) : '-'}
                    </span>
                    <ListMetricVariation metricVariation={subscribersVariation} id={index} tooltip />
                  </>
                ) : (
                  withoutData
                )}
              </span>
              {providerName === 'instagram' && (
                <span className="CreatorListItem-text">
                  <T keyName="CreatorListItem-metricSubscribers">Subscribers</T>
                </span>
              )}
            </div>

            <div className="CreatorListItem-subscribers">
              <span>
                {providerName === 'facebook' ? (
                  <>
                    <span data-value={subscribers}>
                      {subscribers !== null ? humanizeLargeNumber(subscribers, 1) : '-'}
                    </span>
                    <ListMetricVariation metricVariation={subscribersVariation} id={index} tooltip />
                  </>
                ) : (
                  withoutData
                )}
              </span>
              {providerName === 'facebook' && (
                <span className="CreatorListItem-text">
                  <T keyName="CreatorListItem-metricSubscribers">Subscribers</T>
                </span>
              )}
            </div>

            {FeatureFlagsStore.featureFlagsValues['istiktokenabled'] && (
              <div className="CreatorListItem-subscribers">
                <span>
                  {providerName === 'tiktok' ? (
                    <>
                      <span data-value={subscribers}>
                        {subscribers !== null ? humanizeLargeNumber(subscribers, 1) : '-'}
                      </span>
                      <ListMetricVariation metricVariation={subscribersVariation} id={index} tooltip />
                    </>
                  ) : (
                    withoutData
                  )}
                </span>
                {providerName === 'tiktok' && (
                  <span className="CreatorListItem-text">
                    <T keyName="CreatorListItem-metricSubscribers">Subscribers</T>
                  </span>
                )}
              </div>
            )}

            <div className="CreatorListItem-subscribers">
              <span>
                {providerName === 'twitch' ? (
                  <>
                    <span data-value={subscribers}>
                      {subscribers !== null ? humanizeLargeNumber(subscribers, 1) : '-'}
                    </span>
                    <ListMetricVariation metricVariation={subscribersVariation} id={index} tooltip />
                  </>
                ) : (
                  withoutData
                )}
              </span>
              {providerName === 'twitch' && (
                <span className="CreatorListItem-text">
                  <T keyName="CreatorListItem-metricFollowers">Followers</T>
                </span>
              )}
            </div>
          </>
        )}

        <div className="CreatorListItem-total-views">
          <span>
            {providerName === 'youtube' ? (
              <>
                <span data-value={views}>{humanizeLargeNumber(views, 1)}</span>
                <ListMetricVariation metricVariation={viewsVariation} id={index} tooltip />
              </>
            ) : (
              withoutData
            )}
          </span>
          {providerName === 'youtube' && (
            <span className="CreatorListItem-text">
              <T keyName="CreatorListItem-metricViews">Views</T>
            </span>
          )}
        </div>

        {!customListItem && (
          <>
            <div className="CreatorListItem-total-views">
              <span>
                {providerName.toLowerCase() === 'instagram' ? (
                  <>
                    <span data-value={views}>{humanizeLargeNumber(views, 1)}</span>
                    <ListMetricVariation metricVariation={viewsVariation} id={index} tooltip />
                  </>
                ) : (
                  withoutData
                )}
              </span>
              {providerName.toLowerCase() === 'instagram' && (
                <span className="CreatorListItem-text">
                  <T keyName="CreatorListItem-metricViews">Views</T>
                </span>
              )}
            </div>

            {FeatureFlagsStore.featureFlagsValues['istiktokenabled'] && (
              <div className="CreatorListItem-total-views">
                <span>
                  {providerName === 'tiktok' ? (
                    <>
                      <span data-value={views}>{humanizeLargeNumber(views, 1)}</span>
                      <ListMetricVariation metricVariation={viewsVariation} id={index} tooltip />
                    </>
                  ) : (
                    withoutData
                  )}
                </span>

                {providerName === 'tiktok' && (
                  <span className="CreatorListItem-text">
                    <T keyName="CreatorListItem-metricViews">Views</T>
                  </span>
                )}
              </div>
            )}

            <div className="CreatorListItem-total-views">
              <span>
                {providerName === 'twitch' ? (
                  <>
                    <span data-value={views}>{humanizeLargeNumber(views, 1)}</span>
                    <ListMetricVariation metricVariation={viewsVariation} id={index} tooltip />
                  </>
                ) : (
                  withoutData
                )}
              </span>

              {providerName === 'twitch' && (
                <span className="CreatorListItem-text">
                  <T keyName="CreatorListItem-metricViews">Views</T>
                </span>
              )}
            </div>
          </>
        )}

        {customListItem && (
          <>
            <div className="CreatorListItem-total CreatorListItem-total-likes">
              <span>
                <span data-value={likes}>{humanizeLargeNumber(likes, 1)}</span>
                <ListMetricVariation metricVariation={likesVariation} id={index} tooltip />
              </span>
              <span className="CreatorListItem-text">
                <T keyName="CreatorListItem-metricLikes">Likes</T>
              </span>
            </div>

            <div className="CreatorListItem-total CreatorListItem-total-videos">
              <span>
                <span data-value={videos}>{humanizeLargeNumber(videos, 1)}</span>
                <ListMetricVariation metricVariation={videosVariation} id={index} tooltip />
              </span>
              <span className="CreatorListItem-text">
                <T keyName="CreatorListItem-metricPublishedVideos">Published Videos</T>
              </span>
            </div>
          </>
        )}

        <div className="CreatorListItem-actions">{!isViewerUser && this.renderActionButtons()}</div>
      </div>
    )
  }

  renderActionButtons() {
    const _isBrand = this.isBrand()
    const tooltipText = _isBrand ? 'Unmark as Brand' : 'Mark as Brand'
    const tooltipContainerClass = classNames('brand-flag tooltipped tooltipped-n tooltipped-multiline', {
      isBrand: _isBrand,
    })

    return (
      <>
        {this.isAdminUser && (
          <div onClick={this.flagBrand} aria-label={tooltipText} className={tooltipContainerClass}>
            {_isBrand && <span className="close-icon" />} <span>B</span>
          </div>
        )}
      </>
    )
  }

  render() {
    const {
      onCreatorClickCallback,
      index,
      title,
      className,
      id,
      onClick,
      data,
      groupId,
      provider,
      isSelected,
      ...rest
    } = this.props

    if (onClick) {
      return (
        <div className={classNames('CreatorListItem', { selected: isSelected })}>
          <div onClick={() => onClick(data)}>{this.renderCard()}</div>
        </div>
      )
    }

    const to = makeUrlTo.creator({ groupId, provider, creatorId: id })
    return (
      <div className={classNames('CreatorListItem', { selected: isSelected })}>
        <Link
          to={to}
          className={className}
          {...rest}
          onClick={() => onCreatorClickCallback?.({ title, provider, index })}
        >
          {this.renderCard()}
        </Link>
      </div>
    )
  }
}

CreatorListItem.propTypes = {
  title: string,
  country: string,
  thumbnail: string,
  provider: string,
  followers: number,
  engagement: oneOfType([number, string]),
  views: oneOfType([number, string]),
  isBrand: bool,
  customListItem: bool,
}

CreatorListItem.defaultProps = {
  customListItem: false,
}

CreatorListItem.displayName = 'CreatorListItem'

export default CreatorListItem
